import clsx from "clsx";
import styles from "./Table.module.css";

interface TableInterface {
  item: any;
  url: string;
  classes: any;
  onParagraphClick: (url: string) => void;
  foldable?: boolean;
}

const Table = ({
  item,
  url,
  classes,
  onParagraphClick,
  foldable,
}: TableInterface) => {
  const tableHeader = foldable ? (
    <div
      style={{
        backgroundColor: item.url === url ? "white" : undefined,
      }}
      onClick={() => onParagraphClick(item.url)}
      dangerouslySetInnerHTML={{ __html: item.headline }}
      className={clsx(
        classes.description,
        styles.td,
        styles.clickable,
        styles.header,
        styles.foldableHeader
      )}
    />
  ) : (
    <td
      style={{
        backgroundColor: item.url === url ? "white" : undefined,
      }}
      colSpan={2}
      onClick={() => onParagraphClick(item.url)}
      dangerouslySetInnerHTML={{ __html: item.headline }}
      className={clsx(
        classes.description,
        styles.td,
        styles.clickable,
        styles.header
      )}
    />
  );

  const tableRows = item.rows.map((r: any) => {
    const data = r.content.map((p: any) => {
      return (
        <div
          onClick={() => onParagraphClick(p.url)}
          className={clsx(classes.description, classes.clickable)}
          dangerouslySetInnerHTML={{ __html: p.content }}
          style={{ backgroundColor: p.url === url ? "white" : undefined }}
        ></div>
      );
    });

    return (
      <tr className={styles.tr}>
        <td
          onClick={() => onParagraphClick(r.level.url)}
          className={clsx(
            styles.td,
            classes.description,
            classes.clickable,
            classes.tableCellContent
          )}
          dangerouslySetInnerHTML={{ __html: r.level.name }}
          style={{ backgroundColor: r.level.url === url ? "white" : undefined }}
        />
        <td className={clsx(styles.contenttd, classes.tableCellContent)}>
          {data}
        </td>
      </tr>
    );
  });

  const tableFooter = item.footnotes.map((f: any) => {
    return (
      <div
        className={clsx(styles.footerItem, styles.text, styles.clickable)}
        dangerouslySetInnerHTML={{ __html: f.content }}
        onClick={() => onParagraphClick(f.url)}
        key={f.url}
        style={{ backgroundColor: f.url === url ? "white" : undefined }}
      />
    );
  });

  if (foldable) {
    return (
      <>
        <div>
          <details className={styles.foldableTable}>
            <summary>{tableHeader}</summary>
            <table className={styles.table}>
              <tbody>{tableRows}</tbody>
            </table>
          </details>
        </div>
        <details className={styles.footer}>
          <summary>Poznámky pod čarou</summary>
          {tableFooter}
        </details>
      </>
    );
  }

  return (
    <>
      <table
        className={styles.table}
        style={{
          borderBottom:
            tableFooter.length !== 0 ? undefined : "1px solid black",
        }}
      >
        <tbody>
          <tr>{tableHeader}</tr>
          {tableRows}
        </tbody>
      </table>
      {tableFooter.length !== 0 && (
        <details className={styles.footer}>
          <summary>Poznámky pod čarou</summary>
          {tableFooter}
        </details>
      )}
    </>
  );
};

interface RRZJSubTableInterface {
  data: any;
  classes: any;
  onParagraphClick: (url: string) => void;
  url: string;
  headline_urls: any;
  levels: any;
}

const RRZJRows = ({
  data,
  classes,
  onParagraphClick,
  url,
  headline_urls,
  levels,
}: RRZJSubTableInterface) => {
  return (
    <>
      {Object.keys(data.children).map((k, i) => {
        const content = data.children[k].map((c: any) => (
          <p
            onClick={() => onParagraphClick(c.url)}
            className={clsx(classes.description, classes.clickable)}
            dangerouslySetInnerHTML={{ __html: c.content }}
            style={{ backgroundColor: c.url === url ? "white" : undefined }}
          ></p>
        ));

        return (
          <tr className={styles.tr}>
            {i === 0 && (
              <td
                className={clsx(
                  styles.td,
                  styles.rrzjleveltd,
                  classes.description,
                  classes.clickable,
                  classes.tableCellContent
                )}
                onClick={() => onParagraphClick(levels[data.level])}
                style={{
                  backgroundColor:
                    levels[data.level] === url ? "white" : undefined,
                }}
                rowSpan={5}
              >
                {data.level}
              </td>
            )}
            <td
              className={clsx(
                styles.td,
                classes.description,
                classes.clickable,
                classes.tableCellContent
              )}
              onClick={() => onParagraphClick(headline_urls[k])}
              style={{
                backgroundColor: headline_urls[k] === url ? "white" : undefined,
              }}
            >
              {k}
            </td>
            <td className={clsx(styles.contenttd, classes.tableCellContent)}>
              {content}
            </td>
          </tr>
        );
      })}
    </>
  );
};

interface RRZJTableInterface {
  data: any;
  onParagraphClick: (url: string) => void;
  url: string;
  classes: any;
  english?: boolean;
}

interface TableHeadingInterface {
  data: any;
  url: string;
  onClick: (url: string) => void;
  classes: any;
  style?: React.CSSProperties;
}

const TableHeading = ({
  data,
  url,
  onClick,
  classes,
  style,
}: TableHeadingInterface) => {
  return (
    <td
      colSpan={3}
      onClick={() => onClick(data.url)}
      className={clsx(
        styles.td,
        styles.rrzjSubHeading,
        classes.description,
        classes.clickable,
        classes.tableCellContent
      )}
      style={{
        backgroundColor: url === data.url ? "white" : "",
        color: url === data.url ? "black" : undefined,
        ...style,
      }}
    >
      {data.text}
    </td>
  );
};

export const RRZJTable = ({
  data,
  onParagraphClick,
  url,
  classes,
  english,
}: RRZJTableInterface) => {
  const englishTitles = {
    title: "Global scale for sign languages (G)",
    proficient: "Proficient  language user",
    basic: "Basic user",
    leftOutInfo: "C1 - A2 left out for reasons of space",
    references: "Table references",
  };

  const czechTitles = {
    title: "Globální stupnice pro znakové jazyky (G)",

    proficient: "Zkušený uživatel jazyka",
    prificientURL: data.persons.zkuseny,

    independent: "Samostatný uživatel jazyka",
    independentURL: data.persons.samostatny,

    basic: "Uživatel základů jazyka",
    basicURL: data.persons.zaklady,

    references: "Reference tabulky",
  };

  const titles = english ? englishTitles : czechTitles;

  return (
    <>
      <h3
        onClick={() => onParagraphClick(data.url)}
        className={classes.clickable}
        style={{ backgroundColor: url === data.url ? "white" : undefined }}
      >
        {titles.title}
      </h3>
      <table className={styles.table}>
        <tbody>
          <tr className={styles.tr} style={{ borderTop: "1px solid black" }}>
            <TableHeading
              data={{
                text: titles.proficient,
                url: data.persons.zkuseny,
              }}
              url={url}
              onClick={onParagraphClick}
              classes={classes}
            />
          </tr>
          {data.children
            .filter((c: any) => c.level === "C1" || c.level === "C2")
            .map((cdata: any) => (
              <>
                <RRZJRows
                  data={cdata}
                  headline_urls={data.headline_urls}
                  classes={classes}
                  onParagraphClick={onParagraphClick}
                  url={url}
                  levels={data.levels}
                />
              </>
            ))}
          {english ? (
            <tr className={styles.tr} style={{ borderTop: "1px solid black" }}>
              <TableHeading
                data={{
                  text: englishTitles.leftOutInfo,
                  url: "",
                }}
                url={url}
                onClick={onParagraphClick}
                classes={classes}
              />
            </tr>
          ) : (
            <>
              <tr
                className={styles.tr}
                style={{ borderTop: "1px solid black" }}
              >
                <TableHeading
                  data={{
                    text: czechTitles.independent,
                    url: data.persons.samostatny,
                  }}
                  url={url}
                  onClick={onParagraphClick}
                  classes={classes}
                />
              </tr>
              {data.children
                .filter((c: any) => c.level === "B1" || c.level === "B2")
                .map((cdata: any) => (
                  <>
                    <RRZJRows
                      data={cdata}
                      headline_urls={data.headline_urls}
                      classes={classes}
                      onParagraphClick={onParagraphClick}
                      url={url}
                      levels={data.levels}
                    />
                  </>
                ))}
            </>
          )}
          <tr className={styles.tr} style={{ borderTop: "1px solid black" }}>
            <TableHeading
              data={{
                text: titles.basic,
                url: data.persons.zaklady,
              }}
              url={url}
              onClick={onParagraphClick}
              classes={classes}
            />
          </tr>
          {data.children
            .filter((c: any) => c.level === "A1" || c.level === "A2")
            .map((cdata: any) => (
              <>
                <RRZJRows
                  data={cdata}
                  headline_urls={data.headline_urls}
                  classes={classes}
                  onParagraphClick={onParagraphClick}
                  url={url}
                  levels={data.levels}
                />
              </>
            ))}
        </tbody>
      </table>

      <div style={{ border: "1px solid black", padding: "1rem" }}>
        {data.footnotes.map((d: any) => {
          return (
            <div
              onClick={() => onParagraphClick(d.url)}
              className={clsx(classes.description, classes.clickable)}
              dangerouslySetInnerHTML={{ __html: d.content }}
              style={{ backgroundColor: d.url === url ? "white" : undefined }}
            ></div>
          );
        })}
      </div>
    </>
  );
};

export default Table;
